import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@hooks/useDebounce';
import { useForm } from '@hooks/useForm';
import { apiService } from '@services/api';
import { useSnackbar } from '@components/SnackbarNotification';
import { useLoading } from '@contexts/LoadingContext';
import { validateCNPJ, cleanCNPJ } from '@utils/cnpjValidator';
import logger from '@utils/logger';
import { cepService, CEPData, CEPApiUnavailableError } from '@services/cepService';
import { BRAZILIAN_STATES } from '@utils/brazilianStates';
import { ROUTES, MESSAGES, CONFIG } from '@/constants';
import { validatePasswordFrontend } from '@utils/passwordValidator';
import { useAuth } from '@contexts/AuthContext';

export type PackageType = 'condominio' | 'gestao' | 'institucional';

interface SignupErrorLike {
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: {
      errorsList?: Array<{ field: string; message: string }>;
      errors?: Record<string, unknown>;
      message?: string;
      code?: string;
    };
  };
}

export type SignupFormValues = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  cnpj: string;
  phone: string;
  paymentDay: string;
  logo: string | null;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  website: string;
  extraUsersCount: number;
  contractedClientCapacity: number;
  contractedNucleusCapacity: number;
  packageType: PackageType;
};

const initialValues: SignupFormValues = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  cnpj: '',
  phone: '',
  paymentDay: '',
  logo: null,
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  website: '',
  extraUsersCount: 0,
  contractedClientCapacity: 0,
  contractedNucleusCapacity: 0,
  packageType: 'gestao',
};

const validateSignup = (values: SignupFormValues): Partial<Record<keyof SignupFormValues, string>> => {
  const errors: Partial<Record<keyof SignupFormValues, string>> = {};

  // Regras espelhadas de Login.tsx (validateField)
  if (!values.name.trim()) {
    errors.name = 'Nome completo é obrigatório';
  } else if (values.name.trim().length < 3) {
    errors.name = 'Nome deve ter pelo menos 3 caracteres';
  } else if (values.name.trim().length > 100) {
    errors.name = 'Nome deve ter no máximo 100 caracteres';
  }

  if (!values.email.trim()) {
    errors.email = 'Email é obrigatório';
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email.trim())) {
      errors.email = 'Email inválido';
    }
  }

  if (!values.companyName.trim()) {
    errors.companyName = 'Nome da empresa é obrigatório';
  } else if (values.companyName.trim().length < 3) {
    errors.companyName = 'Nome da empresa deve ter pelo menos 3 caracteres';
  } else if (values.companyName.trim().length > 200) {
    errors.companyName = 'Nome da empresa deve ter no máximo 200 caracteres';
  }

  if (!values.cnpj.trim()) {
    errors.cnpj = 'CNPJ é obrigatório';
  } else {
    const cleaned = cleanCNPJ(values.cnpj);
    if (cleaned.length !== 14) {
      errors.cnpj = 'CNPJ deve conter 14 caracteres';
    } else if (!validateCNPJ(cleaned)) {
      errors.cnpj = 'CNPJ inválido';
    }
  }

  if (values.phone.trim()) {
    const phoneCleaned = values.phone.replace(/\D/g, '');
    if (phoneCleaned.length < 10 || phoneCleaned.length > 11) {
      errors.phone = 'Telefone deve ter 10 ou 11 dígitos';
    }
  }

  if (!values.paymentDay.trim()) {
    errors.paymentDay = 'Dia de pagamento é obrigatório';
  } else {
    const day = parseInt(values.paymentDay, 10);
    if (isNaN(day) || ![5, 10, 15, 20, 25].includes(day)) {
      errors.paymentDay = 'Dia de pagamento deve ser 5, 10, 15, 20 ou 25';
    }
  }

  if (!values.street.trim()) {
    errors.street = 'Rua é obrigatória';
  } else if (values.street.trim().length < 3) {
    errors.street = 'Rua deve ter pelo menos 3 caracteres';
  }

  if (!values.number.trim()) {
    errors.number = 'Número é obrigatório';
  }

  if (!values.neighborhood.trim()) {
    errors.neighborhood = 'Bairro é obrigatório';
  } else if (values.neighborhood.trim().length < 2) {
    errors.neighborhood = 'Bairro deve ter pelo menos 2 caracteres';
  }

  if (!values.city.trim()) {
    errors.city = 'Cidade é obrigatória';
  } else if (values.city.trim().length < 2) {
    errors.city = 'Cidade deve ter pelo menos 2 caracteres';
  }

  if (!values.state.trim()) {
    errors.state = 'Estado é obrigatório';
  } else {
    const stateValue = values.state.trim().toUpperCase();
    if (stateValue.length !== 2) {
      errors.state = 'Estado deve ter 2 caracteres (ex: SP, RJ)';
    } else {
      const isValidState = BRAZILIAN_STATES.some(s => s.value === stateValue);
      if (!isValidState) {
        errors.state = 'Estado inválido';
      }
    }
  }

  if (!values.zipCode.trim()) {
    errors.zipCode = 'CEP é obrigatório';
  } else {
    const zipCodeCleaned = values.zipCode.replace(/\D/g, '');
    if (zipCodeCleaned.length !== 8) {
      errors.zipCode = 'CEP deve ter 8 dígitos';
    }
  }

  if (values.website.trim()) {
    try {
      let urlToValidate = values.website.trim();
      if (!urlToValidate.startsWith('http://') && !urlToValidate.startsWith('https://')) {
        urlToValidate = 'https://' + urlToValidate;
      }
      new URL(urlToValidate);
    } catch {
      errors.website = 'URL inválida (ex: https://www.exemplo.com.br)';
    }
  }

  if (!values.password) {
    errors.password = 'Senha é obrigatória';
  } else {
    const passwordValidation = validatePasswordFrontend(values.password);
    if (!passwordValidation.isValid) {
      // Mostrar apenas a primeira mensagem para o campo, mas todas podem ir no snackbar se necessário
      errors.password = passwordValidation.errors[0] || 'Senha inválida';
    }
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Confirmação de senha é obrigatória';
  } else if (values.confirmPassword !== values.password) {
    errors.confirmPassword = 'As senhas não coincidem';
  }

  if (values.packageType === 'gestao' && (values.contractedClientCapacity || 0) < 1) {
    errors.contractedClientCapacity = 'Selecione a quantidade de condomínios para o pacote VOVER Gestão';
  }
  if (values.packageType === 'institucional' && (values.contractedNucleusCapacity || 0) < 1) {
    errors.contractedNucleusCapacity = 'Selecione a quantidade de núcleos para o pacote VOVER Institucional';
  }

  return errors;
};

export const useSignupForm = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { startLoading, updateLoadingMessage, updateLoadingProgress, stopLoading } = useLoading();
  const { login } = useAuth();

  const form = useForm<SignupFormValues>(initialValues, validateSignup);
  const { values, setValues, errors, setErrors } = form;

  const [signupError, setSignupError] = useState('');
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [isCheckingCNPJ, setIsCheckingCNPJ] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [signupVerificationId, setSignupVerificationId] = useState<string | null>(null);

  const debouncedCnpj = useDebounce(values.cnpj, CONFIG.DEBOUNCE_DELAY);

  // Verificar CNPJ (disponibilidade)
  useEffect(() => {
    const checkCNPJAvailability = async () => {
      if (!debouncedCnpj || debouncedCnpj.trim() === '') {
        return;
      }

      const cleaned = cleanCNPJ(debouncedCnpj);

      if (cleaned.length === 14 && validateCNPJ(cleaned)) {
        setIsCheckingCNPJ(true);
        try {
          const response = await apiService.checkCNPJ(cleaned);
          if (response.data.success) {
            if (!response.data.available) {
              setErrors(prev => ({ ...prev, cnpj: 'CNPJ já está em uso' }));
            } else {
              setErrors(prev => {
                if (prev.cnpj === 'CNPJ já está em uso') {
                  return { ...prev, cnpj: '' };
                }
                return prev;
              });
            }
          }
        } catch (error: unknown) {
          const err = error as SignupErrorLike;
          if (err.response?.status !== 400) {
            logger.error('Erro ao verificar CNPJ', error);
          }
        } finally {
          setIsCheckingCNPJ(false);
        }
      }
    };

    checkCNPJAvailability();
  }, [debouncedCnpj, setErrors]);

  const handleBuscarCEP = async (cep: string) => {
    if (!cepService.validarCEP(cep)) {
      return;
    }

    try {
      setBuscandoCEP(true);
      const cepData: CEPData = await cepService.buscarCEP(cep);

      if (!cepData.logradouro || !cepData.bairro || !cepData.localidade) {
        showSnackbar(MESSAGES.WARNINGS.CEP_INCOMPLETE, 'warning');
        return;
      }

      const capitalizeText = (text: string): string =>
        text
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

      setValues(prev => ({
        ...prev,
        street: capitalizeText(cepData.logradouro),
        number: '',
        neighborhood: capitalizeText(cepData.bairro),
        city: capitalizeText(cepData.localidade),
        state: cepData.uf.toUpperCase(),
        zipCode: cepService.formatarCEP(cepData.cep),
      }));

      showSnackbar(MESSAGES.SUCCESS.ADDRESS_FILLED, 'success');
    } catch (error: unknown) {
      if (cepService.validarCEP(cep)) {
        // Verifica se é erro de API indisponível
        if (error instanceof CEPApiUnavailableError) {
          showSnackbar(MESSAGES.ERRORS.CEP_API_UNAVAILABLE, 'error');
        } else {
          showSnackbar(MESSAGES.WARNINGS.CEP_NOT_FOUND, 'warning');
        }
      }
    } finally {
      setBuscandoCEP(false);
    }
  };

  const handleSubmit = async (acceptedTermIds: string[], verifiedSignupId?: string) => {
    setSignupError('');

    const isValid = form.validateForm();
    if (!isValid) {
      const firstError = Object.values(errors).find(error => error);
      if (firstError) {
        setSignupError(firstError);
      }
      return;
    }

    setIsSignupLoading(true);

    const SIGNUP_STEPS = [
      'Validando informações...',
      'Criando conta e ambiente...',
      'Configurando usuário administrador...',
      'Aplicando configurações iniciais (demandas, relatórios, e-mail)...',
      'Finalizando...',
    ];
    const SIGNUP_PROGRESS = [10, 25, 40, 55, 70];

    startLoading(SIGNUP_STEPS[0]);
    updateLoadingProgress(10);
    let stepIndex = 0;
    const stepInterval = setInterval(() => {
      stepIndex = Math.min(stepIndex + 1, SIGNUP_STEPS.length - 1);
      const message = SIGNUP_STEPS[stepIndex];
      if (message) updateLoadingMessage(message);
      const percent = SIGNUP_PROGRESS[stepIndex];
      if (percent !== undefined) updateLoadingProgress(percent);
    }, 2800);

    try {
      const cleanedCnpj = cleanCNPJ(values.cnpj);
      const paymentDayValue = values.paymentDay ? parseInt(values.paymentDay, 10) : undefined;

      let normalizedLogo = values.logo || null;
      if (normalizedLogo && !normalizedLogo.startsWith('data:')) {
        if (normalizedLogo.startsWith('image/')) {
          normalizedLogo = `data:${normalizedLogo}`;
        }
      }

      let normalizedWebsite = values.website?.trim() || null;
      if (normalizedWebsite && !normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
        normalizedWebsite = 'https://' + normalizedWebsite;
      }

      const timezone = typeof Intl !== 'undefined' && Intl.DateTimeFormat?.().resolvedOptions?.().timeZone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;

      const response = await apiService.signup({
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        password: values.password,
        companyName: values.companyName.trim(),
        cnpj: cleanedCnpj,
        phone: values.phone ? values.phone.replace(/\D/g, '') : '',
        paymentDay: paymentDayValue,
        logo: normalizedLogo,
        street: values.street.trim(),
        number: values.number.trim(),
        complement: values.complement.trim() || null,
        neighborhood: values.neighborhood.trim(),
        city: values.city.trim(),
        state: values.state.trim().toUpperCase(),
        zipCode: values.zipCode.replace(/\D/g, ''),
        website: normalizedWebsite,
        extraUsersCount: values.extraUsersCount || 0,
        contractedClientCapacity: values.contractedClientCapacity || 0,
        contractedNucleusCapacity: values.contractedNucleusCapacity || 0,
        packageType: values.packageType || 'condominio',
        acceptedTermIds: acceptedTermIds,
        timezone,
        signupVerificationId: verifiedSignupId || signupVerificationId,
      });

      clearInterval(stepInterval);
      updateLoadingMessage('Entrando no sistema...');
      updateLoadingProgress(85);

      if (response.data.success) {
        showSnackbar(MESSAGES.SUCCESS.SIGNUP_AND_LOGIN, 'success');

        const loginSuccess = await login(
          values.email.trim().toLowerCase(),
          values.password,
        );

        updateLoadingProgress(100);
        if (loginSuccess) {
          showSnackbar(MESSAGES.SUCCESS.LOGIN, 'success');
          navigate(ROUTES.APP);
        } else {
          showSnackbar(MESSAGES.SUCCESS.SIGNUP, 'success');
          navigate(ROUTES.LOGIN);
        }
      } else {
        const message = response.data.message || 'Erro ao criar conta. Tente novamente.';
        setSignupError(message);
        showSnackbar(message, 'error');
      }
    } catch (error: unknown) {
      logger.error('Erro ao criar conta', error);
      const err = error as SignupErrorLike;

      const isTimeout =
        err.code === 'ECONNABORTED' ||
        err.message?.includes('timeout') ||
        err.message?.includes('exceeded');
      if (isTimeout) {
        clearInterval(stepInterval);
        stopLoading();
        setSignupError(
          'A requisição demorou mais que o esperado. Tentando fazer login com sua conta...'
        );
        showSnackbar('Verificando se sua conta foi criada...', 'info');
        try {
          const loginSuccess = await login(
            values.email.trim().toLowerCase(),
            values.password,
          );
          if (loginSuccess) {
            showSnackbar(MESSAGES.SUCCESS.SIGNUP_AND_LOGIN, 'success');
            navigate(ROUTES.APP);
            return;
          }
        } catch (_loginErr) {
          logger.debug('Login após timeout do signup falhou', _loginErr);
        }
        const timeoutMessage =
          'A criação da conta pode ter demorado. Sua conta pode já existir — tente fazer login com seu e-mail e senha na tela de login.';
        setSignupError(timeoutMessage);
        showSnackbar(timeoutMessage, 'warning');
        return;
      }

      const data = err.response?.data;
      const messages: string[] = [];

      const traduzirMensagemCampo = (field: string, rawMessage: string): string => {
        let msg = rawMessage;
        msg = msg.replace('is not allowed to be empty', 'não pode ficar em branco');
        msg = msg.replace('is required', 'é obrigatório');
        msg = msg.replace('must be a valid email', 'deve ser um email válido');
        return msg;
      };

      // 1) errorsList: [{ field, message }]
      if (Array.isArray(data?.errorsList)) {
        data?.errorsList?.forEach((e) => {
          const field = e.field as string;
          const fieldLabel =
            field === 'password'
              ? 'Senha'
              : field === 'email'
              ? 'Email'
              : field === 'name'
              ? 'Nome'
              : field;

          const mensagemCampo = traduzirMensagemCampo(field, e.message);
          messages.push(`${fieldLabel}: ${mensagemCampo}`);
        });
      }

      // 2) errors: { field: message }
      if (data?.errors && typeof data.errors === 'object') {
        Object.entries(data.errors).forEach(([field, msg]) => {
          const fieldLabel =
            field === 'password'
              ? 'Senha'
              : field === 'email'
              ? 'Email'
              : field === 'name'
              ? 'Nome'
              : field;

          const mensagemCampo = traduzirMensagemCampo(field, String(msg));
          messages.push(`${fieldLabel}: ${mensagemCampo}`);
        });
      }

      let baseMessage = data?.message || 'Erro ao criar conta. Tente novamente.';
      // Traduzir mensagens genéricas em inglês vindas do backend
      if (baseMessage === 'Validation error') {
        baseMessage = 'Erro de validação';
      }

      const detalhes = messages.length ? messages.map((msg) => `• ${msg}`).join('\n') : '';
      const message = detalhes ? `${baseMessage}\n${detalhes}` : baseMessage;

      setSignupError(message);
      showSnackbar(message, 'error');
    } finally {
      clearInterval(stepInterval);
      stopLoading();
      setIsSignupLoading(false);
    }
  };

  return {
    form,
    signupError,
    isSignupLoading,
    isCheckingCNPJ,
    buscandoCEP,
    setSignupError,
    setValues,
    handleBuscarCEP,
    handleSubmit,
    signupVerificationId,
    setSignupVerificationId,
  };
};


